import type { CotMePerson, MeResponse } from '../types/community'

export type CotRole = 'parent' | 'kid'

export interface PersonFlags {
  supervised: boolean
  viewOnly: boolean
  first: string
}

/**
 * The account-holder person id. Prefers the explicit family field, then a
 * person flagged `isAccountHolder`, then the first Guardian, then the first
 * person — so the shell always resolves a holder even on a thin payload.
 */
export function resolveAccountHolderId(me: MeResponse): string {
  return (
    me.family.accountHolderPersonId ??
    me.people.find((p) => p.isAccountHolder)?.id ??
    me.people.find((p) => p.kind === 'Guardian')?.id ??
    me.people[0]?.id ??
    ''
  )
}

/** Viewing role follows the selected person (dc.html `selectPerson`). */
export function deriveRole(activePersonId: string | null, accountHolderId: string): CotRole {
  return activePersonId != null && activePersonId === accountHolderId ? 'parent' : 'kid'
}

/** Supervision-derived flags for the active person. */
export function computeFlags(person: CotMePerson | null): PersonFlags {
  return {
    supervised: person?.supervision === 'ApproveEverything',
    viewOnly: person?.supervision === 'ViewOnly',
    first: person?.firstName ?? '',
  }
}

/** The sidebar role line under a person's name. */
export function personRoleLine(
  person: CotMePerson,
  isAccountHolder: boolean,
  managesCount: number,
): string {
  if (isAccountHolder) {
    return 'Adult · you, manages ' + managesCount
  }
  if (person.kind === 'ManagedAdult' || person.supervision === 'NotApplicable') {
    return 'Adult · plays too'
  }
  const age = person.ageYears == null ? '?' : String(person.ageYears)
  const sup =
    person.supervision === 'ViewOnly'
      ? 'supervised, view only'
      : person.supervision === 'SelfSend'
        ? 'own requests'
        : 'supervised'
  return age + ' · ' + sup
}

/** Presence comes from current server evidence for every family member. */
export function isPresenceOnline(person: CotMePerson): boolean {
  return person.online
}

/** The "Viewing as" card headline. */
export function viewingAsLabel(
  activePerson: CotMePerson,
  isAccountHolder: boolean,
  isSolo: boolean,
): string {
  if (!isAccountHolder) {
    const line = personRoleLine(activePerson, false, 0)
    return activePerson.firstName + ', ' + line.split(' ')[0]
  }
  return activePerson.firstName + (isSolo ? ' · adult' : ' (parent)')
}

/** The "Viewing as" supervise note (branches holder / solo / supervised / adult). */
export function superviseNote(params: {
  activePerson: CotMePerson
  isAccountHolder: boolean
  isSolo: boolean
  managedCount: number
  guardianFirst: string
}): string {
  const { activePerson, isAccountHolder, isSolo, managedCount, guardianFirst } = params
  if (isAccountHolder) {
    if (isSolo) {
      return 'A solo adult account, playing under your handle. No profiles to approve for.'
    }
    if (managedCount === 0) {
      return 'Nobody in your family yet. Use the + beside Your Family to set up a profile you manage.'
    }
    const noun = managedCount === 1 ? ' other person' : ' other persons'
    return (
      'You manage ' +
      managedCount +
      noun +
      '. You approve every request they send and see every report they make.'
    )
  }
  if (activePerson.supervision === 'ApproveEverything' || activePerson.supervision === 'ViewOnly') {
    return (
      activePerson.firstName +
      '’s account is supervised by ' +
      guardianFirst +
      '. Everything sent from here needs their yes.'
    )
  }
  return activePerson.firstName + ' is an adult on your account — they play, but cannot approve anyone.'
}
